import type {
    IsoAndUnixTimestamp} from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { idZodSchema } from "@@/shared/id";
import type {
    OrderGroupId} from "@@/shared/order-groups/order-group-id";
import {
    orderGroupIdZodSchema,
} from "@@/shared/order-groups/order-group-id";
import { generateId } from "@@/utilities/generate-id";
import { z } from "zod";

type InitiateFreePaymentCommand = {
    readonly _id: string;
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly _type: "INITIATE_FREE_PAYMENT_COMMAND";
    /**
     * Order group to create payment intent for
     * @type {boolean}
     */
    readonly orderGroupId: OrderGroupId;
    /**
     * Hold money to charge later?
     * @type {boolean}
     */
    readonly delay?: boolean;
};

type InitiateFreePaymentCommandResponse = {
    readonly _id: string;
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly _type: "INITIATE_FREE_PAYMENT_COMMAND_RESPONSE";

    /**
     * Order group payment was created for
     * @type {boolean}
     */
    readonly orderGroupId: OrderGroupId;
};

type CreateCommandProps = Omit<
    InitiateFreePaymentCommand,
    "_id" | "_type" | "_createdAt"
>;
type CreateCommandResponseProps = Omit<
    InitiateFreePaymentCommandResponse,
    "_id" | "_type" | "_createdAt"
>;
const initiateFreePaymentCommandFactory = (params: CreateCommandProps) => {
    const initiateFreePaymentCommand: InitiateFreePaymentCommand = {
        ...params,
        _id: generateId({ prefix: "initiate_free_payment_command__" }),
        _type: "INITIATE_FREE_PAYMENT_COMMAND",
        _createdAt: nowIsoAndUnix(),
    };
    return initiateFreePaymentCommand;
};
const initiateFreePaymentCommandResponseFactory = (
    params: CreateCommandResponseProps,
) => {
    const initiateFreePaymentCommandResponse: InitiateFreePaymentCommandResponse =
        {
            ...params,
            _id: generateId({
                prefix: "initiate_free_payment_command_response__",
            }),
            _type: "INITIATE_FREE_PAYMENT_COMMAND_RESPONSE",
            _createdAt: nowIsoAndUnix(),
        };
    return initiateFreePaymentCommandResponse;
};

const initiateFreePaymentCommandZodSchema: z.ZodSchema<InitiateFreePaymentCommand> =
    z.object({
        _id: idZodSchema,
        _createdAt: isoAndUnixZodSchema,
        _type: z.literal("INITIATE_FREE_PAYMENT_COMMAND"),
        orderGroupId: orderGroupIdZodSchema,
        delay: z.boolean().optional(),
    });

export {
    initiateFreePaymentCommandFactory,
    initiateFreePaymentCommandResponseFactory,
    initiateFreePaymentCommandZodSchema,
};
export type { InitiateFreePaymentCommand, InitiateFreePaymentCommandResponse };
